'use client';

import * as React from 'react';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, Button, Paper, Typography } from '@mui/material';
import { ConfirmProvider } from 'material-ui-confirm';
import { ErrorBoundary } from 'react-error-boundary';
import { Helmet, HelmetProvider } from 'react-helmet-async';

import '@/styles/theme-v2/global.css';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Provider as ReduxProvider } from 'react-redux';

import type { Metadata } from '@/types/v2/metadata';
import { config } from '@/config';
import { applyDefaultSettings } from '@/lib/v2/settings/apply-default-settings';
import { getSettings as getPersistedSettings } from '@/lib/v2/settings/get-settings';
import { UserProvider } from '@/contexts/auth/user-context';
import { PusherProvider } from '@/contexts/pusher/pusher-provider';
import { SettingsProvider } from '@/contexts/settings';
import { Analytics } from '@/components/core/v2/analytics';
import { I18nProvider } from '@/components/core/v2/i18n-provider';
import { LocalizationProvider } from '@/components/core/v2/localization-provider';
import { ThemeProvider } from '@/components/core/v2/theme-provider/theme-provider';
import { ThemedToaster, Toaster } from '@/components/core/v2/toaster';

import { FeatureFlagProvider } from './contexts/feature-flag/feature-flag-provider';
import { store } from './store';

const queryClient = new QueryClient();

const metadata: Metadata = { title: config.site.name };

interface RootProps {
  children: React.ReactNode;
}

export function Root({ children }: RootProps): React.JSX.Element {
  const settings = React.useRef(applyDefaultSettings(getPersistedSettings()));

  const featureFlagConfig = {
    apiKey: config.amplitude.apiKey,
    deploymentKey: config.amplitude.deploymentKey,
  };

  // Retrieve the token (if needed elsewhere) but do not pass it to PusherProvider unless its interface expects it.
  const accessToken = typeof window !== 'undefined' ? (localStorage.getItem('accessToken') ?? undefined) : undefined;

  // Remove authToken from the config (or update the PusherProvider interface if you need it)
  const pusherConfig = {
    pusherKey: config.pusher.key,
    pusherCluster: config.pusher.cluster,
    authEndpoint: `${config.api.base}/broadcasting/auth`,
  };

  return (
    <HelmetProvider>
      <Helmet>
        <meta content={config.site.themeColor} name="theme-color" />
        <title>{metadata.title}</title>
      </Helmet>
      <ReduxProvider store={store}>
        <QueryClientProvider client={queryClient}>
          <UserProvider>
            <PusherProvider {...pusherConfig}>
              <Analytics>
                <LocalizationProvider>
                  <FeatureFlagProvider config={featureFlagConfig}>
                    <SettingsProvider settings={settings.current}>
                      <I18nProvider language="en">
                        <ThemeProvider>
                          <ErrorBoundary FallbackComponent={ErrorFallback}>
                            <ConfirmProvider>{children}</ConfirmProvider>
                            <ThemedToaster position="top-right" />
                          </ErrorBoundary>
                        </ThemeProvider>
                      </I18nProvider>
                    </SettingsProvider>
                  </FeatureFlagProvider>
                </LocalizationProvider>
              </Analytics>
            </PusherProvider>
          </UserProvider>
        </QueryClientProvider>
      </ReduxProvider>
    </HelmetProvider>
  );
}

function ErrorFallback({ error }: { error: Error }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          textAlign: 'center',
          maxWidth: 500,
          width: '100%',
        }}
      >
        <ErrorOutlineIcon color="error" sx={{ fontSize: 60, mb: 2 }} />
        <Typography gutterBottom variant="h5">
          Oops! Something went wrong.
        </Typography>
        <Typography color="textSecondary" sx={{ mb: 3 }} variant="body1">
          We're sorry for the inconvenience. Please try refreshing the page, or contact support if the problem persists.
        </Typography>
        <Button
          onClick={() => {
            window.location.reload();
          }}
          variant="contained"
        >
          Refresh Page
        </Button>
      </Paper>
    </Box>
  );
}
